import React, { Dispatch, SetStateAction, useMemo, useState } from 'react'

import Box from 'components_new/atoms/Box'

import FilterPopover from 'components_new/molecules/FilterPopover'
import SearchAndSelect from 'components_new/molecules/SearchAndSelect'
import { getSortedOptions } from 'components_new/molecules/SelectSearch'
import SettingsGroup from 'components_new/molecules/SettingsGroup'

import { ParsedDashboardFilterOptions } from 'types/GlobalDashboardFilter'
import { useTranslation } from 'language/translator'

export type TFilter = { [key: string]: string[] }

interface FilterProps {
  filter: TFilter
  filterOptions: Map<string, ParsedDashboardFilterOptions>
  setFilter: Dispatch<SetStateAction<TFilter>>
}

const Filter = (props: FilterProps) => {
  const { filter, filterOptions, setFilter } = props
  const { t } = useTranslation()

  const [openFilter, setOpenFilter] = useState<string | null>(null)

  const sortedFilterOptions = useMemo(() => {
    const optionsMap = new Map<string, ReturnType<typeof getSortedOptions>>()

    Array.from(filterOptions.keys()).forEach((key) => {
      const fo = filterOptions.get(key) as ParsedDashboardFilterOptions

      optionsMap.set(
        key,
        getSortedOptions(
          fo.options
            ?.filter((opt): opt is string => opt != null)
            .map((opt) => ({ id: opt, title: opt })) || [],
          false
        )
      )
    })

    return optionsMap
  }, [filterOptions])

  const handleClick = (
    event: React.MouseEvent<HTMLElement>,
    attributeId: string
  ) => {
    setOpenFilter(attributeId)
  }

  const handleClose = () => {
    setOpenFilter(null)
  }

  const handleChange = (attributeId: string, selectedIds: string[]) => {
    if (selectedIds.length === 0) {
      setFilter((prevState) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { [attributeId]: _, ...rest } = prevState

        return rest
      })
    } else {
      setFilter((prevState) => ({
        ...prevState,
        [attributeId]: selectedIds
      }))
    }
  }

  const filterOptionValues = Array.from(filterOptions.values())

  return (
    <SettingsGroup
      title={t('filters', 'filters')}
      helperText={t('dashboards', 'filter_parameters_intro')}
    >
      {filterOptionValues
        .sort((a, b) => a.index - b.index)
        .map((fo) => {
          const loading = !fo.options
          const selectedIds = filter[fo.attribute_id] || []
          const selectedLabel =
            selectedIds.length > 0 ? selectedIds.join(', ') : t('global', 'all')

          return (
            <Box
              component="li"
              key={fo.attribute_id}
              sx={{ listStyle: 'none' }}
            >
              <FilterPopover
                active={selectedIds.length > 0}
                loading={loading}
                onClick={(event) => handleClick(event, fo.attribute_id)}
                onClose={handleClose}
                open={openFilter === fo.attribute_id}
                popoverWidth={300}
                primary={fo.title}
                secondary={selectedLabel}
                tooltipTitle={fo.title}
                tooltipValue={selectedLabel}
              >
                <SearchAndSelect
                  autoFocus={true}
                  multiple={true}
                  onChange={(value) => handleChange(fo.attribute_id, value)}
                  options={sortedFilterOptions.get(fo.attribute_id) || []}
                  size="small"
                  sx={{ p: 2 }}
                  value={selectedIds}
                />
              </FilterPopover>
            </Box>
          )
        })}
    </SettingsGroup>
  )
}

export default Filter
